import { HttpClient, HttpEventType, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import type { GeometryMetricsDocument, Mesh2nurbsMetricsDocument } from '@infinitform/platform-core';
import { Mesh } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { RunType } from '../models/enums';
import { GeometryProcessingService } from './geometry/geometry-processing.service';
import { ProjectConfigurationService } from './project-configuration.service';
import { ProgressService } from './progress.service';

export interface FrequencyEntry {
  index: number;
  value: number;
}

/**
 * The 4 synthetic mode-shape ids a `frequenciesExists` run assigns its
 * loadcase entries, `${i + 1}` for i in 0..3. Real load case ids are 5-char
 * generated ids and never collide with these. Used to tell a mode shape
 * entry apart from a static load case mixed into the same Free Vibration run.
 */
export const FREE_VIBRATION_MODE_SHAPE_IDS: readonly string[] = ['1', '2', '3', '4'];

export type FreqResponseType = 'stress' | 'displacement' | 'acceleration';

export interface PerLcEntries {
  lc: number;
  caseId?: string;
  /**
   * Which quantity the FRF values represent — set by the runner based on the
   * optimizer's output file name (HE runs write Stress/Displacement/
   * Acceleration files depending on the constraint family). Absent on data
   * produced before optimizer 317859c — treat as 'stress'.
   */
  responseType?: FreqResponseType;
  values: FrequencyEntry[];
}

/**
 * Wrap legacy flat `[{index, value}]` JSON as a single-LC per-LC array so
 * downstream consumers can always assume the per-LC shape. New projects ship
 * the per-LC shape directly; old projects on S3 keep working unchanged.
 */
function normalizePerLc(raw: FrequencyEntry[] | PerLcEntries[] | undefined | null): PerLcEntries[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const first = raw[0] as FrequencyEntry | PerLcEntries;
  if (first && typeof first === 'object' && 'values' in first && Array.isArray((first as PerLcEntries).values)) {
    return raw as PerLcEntries[];
  }
  return [{ lc: 0, values: raw as FrequencyEntry[] }];
}

@Injectable({
  providedIn: 'root'
})
export class AssemblyLoaderService {
  private http = inject(HttpClient);
  private projectConfig = inject(ProjectConfigurationService);
  private progressService = inject(ProgressService);
  private geometryProcessing = inject(GeometryProcessingService);

  public loadingProject: boolean = true;
  public loadingProjectStatus: 'download' | 'init' = 'download';

  // Legacy progress properties - kept for backward compatibility
  public assemblyDownloadProgress: number = 0;
  public assemblyInitProgress = { total: 0, current: 0 };

  public parametricAssemblyAvailable = false;
  public organicAssemblyAvailable = false;
  public optimizedInfoAvailable = false;

  // Lazy loading state for optimized assembly info
  public loadCaseDataLoading = false;
  public loadCaseDataProgress = 0;
  public loadCaseDataMessage = '';

  // New version parallel loading
  public frequenciesResult?: any;
  // Flat shape kept as the default view (LC 0). Existing consumers continue to read this.
  public eigenFrequenciesResult?: { index: number; value: number }[];
  public freqResponseResult?: { index: number; value: number }[];
  // Per-LC shape for HE/RV multi-load-case projects. Falls back to a single-entry
  // wrapper for legacy flat-shape projects so old data on S3 keeps working.
  public eigenFrequenciesPerLc?: PerLcEntries[];
  public freqResponsePerLc?: PerLcEntries[];
  public manufacturingCostResult?: { machiningCost: number };
  public geometryMetricsResult?: GeometryMetricsDocument;
  public mesh2nurbsMetricsResult?: Mesh2nurbsMetricsDocument;
  public resultsAreVibrations = false;
  /**
   * True when a `resultsAreVibrations` run also carries static load case
   * results (`organic.staticLoadcases` / `prismatic.staticLoadcases`) — a
   * Free Vibration + Static run. Drives the per-selection vibration/static
   * split; false for a plain Free Vibration run, where every selectable
   * entry is a mode shape.
   */
  public resultsHaveMixedStaticLoadcases = false;

  private loadedLoadCases = new Map<string, boolean>(); // Track which loadcases have been loaded
  /**
   * Per-loadcase in-flight requests — deduplicates concurrent callers for
   * the same key so rapid clicks (lc1 → lc2 → lc1) don't race each other's
   * progress state. Cleared alongside `loadedLoadCases` on every new
   * `getOptimizedAssemblyInfo` run (i.e. on every mf-case swap).
   */
  private loadCaseInflight = new Map<string, Promise<unknown>>();

  /**
   * Byte counters for every load-case fetch currently in flight, keyed the same
   * way as `loadCaseInflight`.
   *
   * `loadCaseDataProgress` is a single scalar bound to one shared progress bar,
   * but load cases are fetched one at a time by the lazy path and several
   * different load cases can overlap (`loadCaseInflight` only dedupes repeats
   * of the *same* key). Each fetch used to reset the scalar to 0 and then write
   * its own percentage into it, so the bar restarted once per load case in the
   * sequential comparison loop, and jumped between two unrelated progressions
   * when loads overlapped.
   *
   * Tracking bytes per fetch and deriving the scalar as a sum gives one honest
   * bar across however many load cases are loading. Per-caller progress is
   * unaffected — `progressCallback` still reports that caller's own fetch,
   * which is what the comparison view's per-panel bars want.
   */
  private loadCaseByteTotals = new Map<string, { loaded: number; total: number }>();

  /**
   * Byte counters for the assembly downloads that make up a single project
   * open: the base assembly plus, on a finished project, the organic and
   * parametric optimized meshes.
   *
   * `assemblyDownloadProgress` drives the full-screen loader bar and was
   * likewise a single scalar that all three downloads overwrote with their own
   * percentage — so the bar ran 0→100 for the base assembly, then restarted for
   * organic, then again for parametric. Now that the optimized meshes are
   * fetched concurrently with the base assembly, they also interleaved writes
   * and made it jump between unrelated progressions.
   *
   * Entries are kept (not deleted) once a download finishes, so completed bytes
   * keep counting toward the total and the bar never travels backwards. The map
   * is cleared per project open by `beginAssemblyDownloadBatch()`.
   */
  private assemblyByteTotals = new Map<string, { loaded: number; total: number }>();

  /**
   * Resets the assembly download aggregate at the start of a project open.
   *
   * Must be called before the first download starts — the optimized meshes are
   * prefetched concurrently with the base assembly, so there is no single
   * download that can safely own the reset.
   */
  beginAssemblyDownloadBatch(): void {
    this.assemblyByteTotals.clear();
    this.assemblyDownloadProgress = 0;
  }

  /** Records byte progress for one assembly download and refreshes the shared bar. */
  private trackAssemblyDownload(key: string, loaded: number, total: number): void {
    this.assemblyByteTotals.set(key, { loaded, total });

    let sumLoaded = 0;
    let sumTotal = 0;
    for (const entry of this.assemblyByteTotals.values()) {
      sumLoaded += entry.loaded;
      sumTotal += entry.total;
    }

    // Before any Content-Length is known there is nothing honest to show;
    // hold at the previous value rather than flashing 99 like the old
    // NaN fallback did.
    if (sumTotal <= 0) return;

    this.assemblyDownloadProgress = parseFloat(((100 * sumLoaded) / sumTotal).toFixed(1));
  }

  /**
   * How many load cases the current batch expects, and how many are done.
   *
   * Byte-summing alone only fixes *overlapping* loads. The comparison view
   * loads its panels in a `for…of` with `await` — strictly one at a time — so
   * without a declared batch size the bar has no way to know a second load
   * case is coming and still restarts per case. A caller that is about to walk
   * N load cases wraps the walk in `beginLoadCaseBatch(N)` / `endLoadCaseBatch()`
   * and gets one continuous 0→100 across the whole run.
   */
  private loadCaseBatchExpected = 0;
  private loadCaseBatchCompleted = 0;

  /**
   * Declare that `expected` load cases are about to be loaded, so the shared
   * progress bar spans the whole run instead of restarting per load case.
   * Safe to nest-free call; always pair with `endLoadCaseBatch()` in a finally.
   */
  beginLoadCaseBatch(expected: number): void {
    this.loadCaseBatchExpected = Math.max(0, expected);
    this.loadCaseBatchCompleted = 0;
    if (this.loadCaseBatchExpected > 0) {
      this.loadCaseDataLoading = true;
      this.loadCaseDataProgress = 0;
      this.loadCaseDataMessage = `Loading load case 1 of ${this.loadCaseBatchExpected}...`;
    }
  }

  /** Ends the batch declared by `beginLoadCaseBatch`. */
  endLoadCaseBatch(): void {
    this.loadCaseBatchExpected = 0;
    this.loadCaseBatchCompleted = 0;
    this.refreshAggregateLoadCaseProgress();
  }

  /** Counts one load case as finished (loaded, cached, or failed) for the batch. */
  private markBatchUnitComplete(): void {
    if (this.loadCaseBatchExpected > 0) {
      this.loadCaseBatchCompleted++;
    }
  }

  /**
   * Recomputes the shared scalar from the declared batch plus every in-flight
   * fetch, and clears the loading flag only once nothing is left.
   */
  private refreshAggregateLoadCaseProgress(): void {
    let loaded = 0;
    let total = 0;
    for (const entry of this.loadCaseByteTotals.values()) {
      loaded += entry.loaded;
      total += entry.total;
    }
    const inFlight = this.loadCaseByteTotals.size;
    const inFlightFraction = total > 0 ? loaded / total : 0;

    if (this.loadCaseBatchExpected > 0) {
      this.loadCaseDataLoading = true;
      const done = this.loadCaseBatchCompleted + (inFlight > 0 ? inFlightFraction : 0);
      this.loadCaseDataProgress = Math.min(100, Math.round((done / this.loadCaseBatchExpected) * 100));
      const current = Math.min(this.loadCaseBatchCompleted + 1, this.loadCaseBatchExpected);
      this.loadCaseDataMessage = `Loading load case ${current} of ${this.loadCaseBatchExpected}...`;
      return;
    }

    if (inFlight === 0) {
      this.loadCaseDataLoading = false;
      this.loadCaseDataProgress = 0;
      return;
    }

    this.loadCaseDataLoading = true;
    this.loadCaseDataProgress = Math.round(inFlightFraction * 100);
    this.loadCaseDataMessage =
      inFlight > 1
        ? `Loading loadcase data (${inFlight} load cases): ${this.geometryProcessing.formatBytes(loaded)} / ${this.geometryProcessing.formatBytes(total)}`
        : `Loading loadcase data: ${this.geometryProcessing.formatBytes(loaded)} / ${this.geometryProcessing.formatBytes(total)}`;
  }

  getAssembly(protoUrl: string): Promise<ArrayBuffer> {
    this.progressService.updateProgress(20, 'Downloading assembly…');

    const request = new HttpRequest('GET', protoUrl, { responseType: 'arraybuffer', reportProgress: true });

    return new Promise((resolve, reject) => {
      this.http.request<ArrayBuffer>(request).subscribe({
        next: (event) => {
          if (event.type === HttpEventType.DownloadProgress) {
            if (event.total) {
              const percentage = (100 / event.total) * event.loaded;
              this.trackAssemblyDownload('base-assembly', event.loaded, event.total);

              // Update progress within assembly loading phase
              // HTTP progress (0-100%) maps to sub-progress within assembly phase (15-50% overall)
              this.progressService.updateProgress(percentage, `Downloading assembly... ${Math.round(percentage)}%`);
            }
          }

          if (event.type === HttpEventType.Response) {
            this.loadingProjectStatus = 'init';
            this.progressService.updateProgress(100, 'Assembly downloaded');
            resolve(event.body as ArrayBuffer);
          }
        },
        error: (err) => reject(err)
      });
    });
  }

  async getOptimizedAssembly(trackProgress = true): Promise<ArrayBuffer> {
    // Safe access: `optimization` may be undefined on brand-new projects or
    // on multi-mfg masters that haven't pre-merged a child yet. The
    // non-null assertion previously here threw instead of falling through
    // to the "No additive_design URL" branch below.
    const url = this.projectConfig.project.optimization?.additive_design;
    console.log('[AssemblyLoader] getOptimizedAssembly — URL:', url || '(empty)');
    if (!url) {
      console.warn('[AssemblyLoader] No additive_design URL — skipping organic assembly');
      throw new Error('No additive_design URL available');
    }
    if (trackProgress) this.progressService.updateTaskProgress('optimized-assembly', 0, 'Downloading optimized assembly…');

    const request = new HttpRequest('GET', url, {
      responseType: 'arraybuffer',
      reportProgress: true
    });

    return new Promise((resolve, reject) => {
      this.http.request<ArrayBuffer>(request).subscribe({
        next: (event) => {
          if (event.type === HttpEventType.DownloadProgress) {
            const percentage = event.total && event.total > 0 ? (100 * event.loaded) / event.total : 0;
            this.trackAssemblyDownload('optimized-assembly', event.loaded, event.total ?? 0);

            // Update task progress
            if (trackProgress) {
              this.progressService.updateTaskProgress(
                'optimized-assembly',
                percentage,
                `Downloading optimized assembly... ${Math.round(percentage)}%`
              );
            }
          } else if (event.type === HttpEventType.Response) {
            this.loadingProjectStatus = 'init';
            if (trackProgress) this.progressService.completeTask('optimized-assembly', 'Optimized assembly downloaded');
            resolve(event.body as ArrayBuffer);
          }
        },
        error: (err) => {
          console.error('Error downloading the assembly:', err);
          reject(err);
        }
      });
    });
  }

  async getOptimizedAssemblyParametric(trackProgress = true): Promise<ArrayBuffer> {
    // Same defensive fix as `getOptimizedAssembly`: guard against
    // undefined `optimization` so the fallthrough-to-"no URL" branch
    // below runs instead of throwing on the `.additive_design_parametric`
    // access.
    const url = this.projectConfig.project.optimization?.additive_design_parametric;
    console.log('[AssemblyLoader] getOptimizedAssemblyParametric — URL:', url || '(empty)');
    if (!url) {
      console.warn('[AssemblyLoader] No additive_design_parametric URL — skipping parametric assembly');
      throw new Error('No additive_design_parametric URL available');
    }
    if (trackProgress) this.progressService.updateTaskProgress('parametric-assembly', 0, 'Downloading parametric assembly…');

    const request = new HttpRequest('GET', url, {
      responseType: 'arraybuffer',
      reportProgress: true
    });

    return new Promise((resolve, reject) => {
      this.http.request<ArrayBuffer>(request).subscribe({
        next: (event) => {
          if (event.type === HttpEventType.DownloadProgress) {
            const percentage = event.total && event.total > 0 ? (100 * event.loaded) / event.total : 0;
            this.trackAssemblyDownload('parametric-assembly', event.loaded, event.total ?? 0);

            // Update task progress
            if (trackProgress) {
              this.progressService.updateTaskProgress(
                'parametric-assembly',
                percentage,
                `Downloading parametric assembly... ${Math.round(percentage)}%`
              );
            }
          } else if (event.type === HttpEventType.Response) {
            this.loadingProjectStatus = 'init';
            if (trackProgress) this.progressService.completeTask('parametric-assembly', 'Parametric assembly downloaded');
            resolve(event.body as ArrayBuffer);
          }
        },
        error: (err) => {
          reject(err);
        }
      });
    });
  }

  // Helper to download with progress tracking
  downloadWithProgress<T>(
    url: string,
    progressTracker: { loaded: number; total: number; fileLoaded: Map<string, number>; fileTotal: Map<string, number> },
    onProgress: () => void,
    responseType: 'json' | 'arraybuffer' = 'json'
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const fileId = url;
      progressTracker.fileLoaded.set(fileId, 0);
      progressTracker.fileTotal.set(fileId, 0);

      this.http
        .get(url, {
          responseType: responseType as any,
          reportProgress: true,
          observe: 'events'
        })
        .subscribe({
          next: (event) => {
            if (event.type === HttpEventType.DownloadProgress) {
              const prevLoaded = progressTracker.fileLoaded.get(fileId) || 0;
              const prevTotal = progressTracker.fileTotal.get(fileId) || 0;

              // Update file-specific progress
              progressTracker.fileLoaded.set(fileId, event.loaded);
              if (event.total) {
                progressTracker.fileTotal.set(fileId, event.total);
              }

              // Recalculate totals from all files
              let totalLoaded = 0;
              let totalSize = 0;
              for (const loaded of progressTracker.fileLoaded.values()) {
                totalLoaded += loaded;
              }
              for (const total of progressTracker.fileTotal.values()) {
                totalSize += total;
              }
              progressTracker.loaded = totalLoaded;
              progressTracker.total = totalSize;

              onProgress();
            } else if (event.type === HttpEventType.Response) {
              resolve(event.body as T);
            }
          },
          error: (err) => reject(err)
        });
    });
  }

  async getOptimizedAssemblyInfo(): Promise<any> {
    console.log('[AssemblyLoader] getOptimizedAssemblyInfo — flowVersion:', this.projectConfig.project.flowVersion);
    console.log('[AssemblyLoader] optimization object:', JSON.stringify(this.projectConfig.project.optimization)?.substring(0, 500));
    this.progressService.updateTaskProgress('optimization-info', 0, 'Loading optimization info…');

    // Reset lazy loading state. Any in-flight per-loadcase fetches from
    // the previous child are abandoned — they'll still resolve but the
    // engine has moved on, so their attribute writes land on now-orphaned
    // entries. The `loadCaseInflight` Map is wiped so new requests run
    // fresh against this child's URLs.
    this.loadedLoadCases.clear();
    this.loadCaseInflight.clear();
    this.geometryProcessing.loadCaseUrls = { organic: [], prismatic: [] };

    // Safe-guard: every v2 branch below uses `optimization!.v2!` non-null
    // assertions. If `optimization` (or `optimization.v2`) is missing, let
    // the caller's Promise.allSettled handle the absence cleanly rather
    // than the access chain throwing a confusing TypeError deep inside.
    if (!this.projectConfig.project.optimization) {
      throw new Error('No optimization data available');
    }

    if (this.projectConfig.project.flowVersion === 2) {
      console.log('[AssemblyLoader] v2 path — organicGeometryUrl:', this.projectConfig.project.optimization?.v2?.organic?.geometryUrl || '(empty)');
      console.log('[AssemblyLoader] v2 path — frequenciesExists:', this.projectConfig.project.optimization?.v2?.frequenciesExists);
      console.log('[AssemblyLoader] v2 path — organic loadcases count:', this.projectConfig.project.optimization?.v2?.organic?.loadcases?.length);
      // Vibrations
      if (this.projectConfig.project.optimization!.v2!.frequenciesExists) {
        this.progressService.updateTaskProgress('optimization-info', 10, 'Loading frequencies data…');
        const frequenciesResponse = await lastValueFrom(
          this.http.get(this.projectConfig.project.optimization!.v2!.frequencies)
        );
        this.frequenciesResult = frequenciesResponse;
        this.progressService.updateTaskProgress('optimization-info', 15, 'Frequencies data loaded');
      }

      // Eigenfrequencies (HE/RV) — accept either legacy flat shape or new per-LC shape.
      // Legacy: [{index, value}, ...]
      // New:    [{lc: 0, caseId?, values: [...]}, {lc: 1, ...}, ...]
      if (this.projectConfig.project.optimization!.v2!.eigenFrequenciesExists) {
        const eigenResponse = await lastValueFrom(
          this.http.get<FrequencyEntry[] | PerLcEntries[]>(this.projectConfig.project.optimization!.v2!.eigenFrequencies!)
        );
        this.eigenFrequenciesPerLc = normalizePerLc(eigenResponse);
        this.eigenFrequenciesResult = this.eigenFrequenciesPerLc[0]?.values ?? [];
      }

      // Frequency response modal stress (HE/RV) — same dual-shape handling.
      if (this.projectConfig.project.optimization!.v2!.freqResponseExists) {
        const frfResponse = await lastValueFrom(
          this.http.get<FrequencyEntry[] | PerLcEntries[]>(this.projectConfig.project.optimization!.v2!.freqResponseModalStress!)
        );
        this.freqResponsePerLc = normalizePerLc(frfResponse);
        this.freqResponseResult = this.freqResponsePerLc[0]?.values ?? [];
      }

      // Manufacturing cost (Milling2 only)
      if (this.projectConfig.project.optimization!.v2!.manufacturingCostExists) {
        const response = await lastValueFrom(
          this.http.get<{ machiningCost: number }>(
            this.projectConfig.project.optimization!.v2!.manufacturingCost!
          )
        );
        this.manufacturingCostResult = response;
      }

      // Geometry metrics sidecar - present for any run whose metrics stage
      // succeeded. Best-effort at the source, so absence is normal and must
      // never fail the results load.
      this.geometryMetricsResult = undefined;
      if (this.projectConfig.project.optimization!.v2!.geometryMetricsExists) {
        try {
          this.geometryMetricsResult = await lastValueFrom(
            this.http.get<GeometryMetricsDocument>(
              this.projectConfig.project.optimization!.v2!.geometryMetrics!
            )
          );
        } catch (e) {
          console.warn('[AssemblyLoader] geometry metrics sidecar unavailable:', e);
        }
      }

      // MeshToNurbs job record - written by the organic CAD step, absent on
      // runs without one or from before it existed. Like the metrics above,
      // it must never fail the results load.
      this.mesh2nurbsMetricsResult = undefined;
      if (this.projectConfig.project.optimization!.v2!.mesh2nurbsMetricsExists) {
        try {
          this.mesh2nurbsMetricsResult = await lastValueFrom(
            this.http.get<Mesh2nurbsMetricsDocument>(
              this.projectConfig.project.optimization!.v2!.mesh2nurbsMetrics!
            )
          );
        } catch (e) {
          console.warn('[AssemblyLoader] mesh2nurbs job record unavailable:', e);
        }
      }

      // Load only the geometry, defer attribute loading
      const organicGenerator = async () => {
        this.progressService.updateTaskProgress('optimization-info', 20, 'Loading organic geometry…');
        const plyData = await lastValueFrom(
          this.http.get(this.projectConfig.project.optimization!.v2!.organic.geometryUrl, {
            responseType: 'arraybuffer'
          })
        );
        const plyGeometry = await this.geometryProcessing.processPLYGeometry(plyData, 20, {
          bvh: true,
          // An FEA run's result mesh is the input CAD, often a few triangles
          // per face; optimized result meshes are left as they are.
          refineForDisplacement: this.projectConfig.project.runType === RunType.FiniteElementAnalysis
        });
        const plyMesh = new Mesh(plyGeometry);
        this.geometryProcessing.organicMesh = plyMesh; // Cache for later use
        this.progressService.updateTaskProgress('optimization-info', 40, 'Organic geometry processed');

        const result: {
          id: string;
          t: Mesh | null;
          x: Mesh | null;
          y: Mesh | null;
          z: Mesh | null;
          s: Mesh | null;
          attributesLoaded?: boolean;
        }[] = [];

        if (this.projectConfig.project.optimization!.v2!.frequenciesExists === true) {
          this.resultsAreVibrations = true;
          // Store URLs for lazy loading
          for (let i = 0; i < 4; i++) {
            const loadcase = this.projectConfig.project.optimization!.v2!.organic.loadcases[i];
            const id = `${i + 1}`;
            this.geometryProcessing.loadCaseUrls.organic.push({
              id,
              x: loadcase.x,
              y: loadcase.y,
              z: loadcase.z,
              t: loadcase.t
            });
            result.push({ id, t: plyMesh, x: plyMesh, y: plyMesh, z: plyMesh, s: plyMesh, attributesLoaded: false });
          }

          // Free Vibration + Static run — static load case results sit
          // alongside the 4 mode shapes, keyed by their real load case id.
          const staticLoadcases = this.projectConfig.project.optimization!.v2!.organic.staticLoadcases ?? [];
          this.resultsHaveMixedStaticLoadcases = this.resultsHaveMixedStaticLoadcases || staticLoadcases.length > 0;
          for (const loadcase of staticLoadcases) {
            this.geometryProcessing.loadCaseUrls.organic.push({
              id: loadcase.id,
              x: loadcase.x,
              y: loadcase.y,
              z: loadcase.z,
              t: loadcase.t,
              s: loadcase.s
            });
            result.push({
              id: loadcase.id,
              t: plyMesh,
              x: plyMesh,
              y: plyMesh,
              z: plyMesh,
              s: plyMesh,
              attributesLoaded: false
            });
          }
        } else {
          // Store URLs for lazy loading
          for (
            let i = 0;
            i < this.projectConfig.project.optimization!.v2!.organic.loadcases.length;
            i++
          ) {
            const loadcase = this.projectConfig.project.optimization!.v2!.organic.loadcases[i];
            this.geometryProcessing.loadCaseUrls.organic.push({
              id: loadcase.id,
              x: loadcase.x,
              y: loadcase.y,
              z: loadcase.z,
              t: loadcase.t,
              s: loadcase.s
            });
            result.push({
              id: loadcase.id,
              t: plyMesh,
              x: plyMesh,
              y: plyMesh,
              z: plyMesh,
              s: plyMesh,
              attributesLoaded: false
            });
          }
        }
        this.progressService.updateTaskProgress('optimization-info', 50, 'Organic loadcase info prepared');
        return result;
      };

      // Prismatic
      const prismaticGenerator = async () => {
        if (!this.projectConfig.project.optimization!.v2!.prismatic) return null;
        this.progressService.updateTaskProgress('optimization-info', 55, 'Loading prismatic geometry…');

        // Download PLY data
        const plyData = await lastValueFrom(
          this.http.get(this.projectConfig.project.optimization!.v2!.prismatic.geometryUrl, {
            responseType: 'arraybuffer'
          })
        );
        // Process in worker (or fallback to main thread)
        const plyGeometry = await this.geometryProcessing.processPLYGeometry(plyData, 20, { bvh: true });
        const plyMesh = new Mesh(plyGeometry);
        this.geometryProcessing.prismaticMesh = plyMesh; // Cache for later use
        this.progressService.updateTaskProgress('optimization-info', 75, 'Prismatic geometry processed');

        const result: {
          id: string;
          t: Mesh | null;
          x: Mesh | null;
          y: Mesh | null;
          z: Mesh | null;
          s: Mesh | null;
          attributesLoaded?: boolean;
        }[] = [];

        if (this.projectConfig.project.optimization!.v2!.frequenciesExists) {
          this.resultsAreVibrations = true;
          // Store URLs for lazy loading
          for (let i = 0; i < 4; i++) {
            const loadcase = this.projectConfig.project.optimization!.v2!.prismatic.loadcases[i];
            const id = `${i + 1}`;
            this.geometryProcessing.loadCaseUrls.prismatic.push({
              id,
              x: loadcase.x,
              y: loadcase.y,
              z: loadcase.z,
              t: loadcase.t
            });
            result.push({
              id,
              t: plyMesh,
              x: plyMesh,
              y: plyMesh,
              z: plyMesh,
              s: plyMesh,
              attributesLoaded: false
            });
          }

          // Free Vibration + Static run — static load case results sit
          // alongside the 4 mode shapes, keyed by their real load case id.
          const staticLoadcases = this.projectConfig.project.optimization!.v2!.prismatic.staticLoadcases ?? [];
          this.resultsHaveMixedStaticLoadcases = this.resultsHaveMixedStaticLoadcases || staticLoadcases.length > 0;
          for (const loadcase of staticLoadcases) {
            this.geometryProcessing.loadCaseUrls.prismatic.push({
              id: loadcase.id,
              x: loadcase.x,
              y: loadcase.y,
              z: loadcase.z,
              t: loadcase.t,
              s: loadcase.s
            });
            result.push({
              id: loadcase.id,
              t: plyMesh,
              x: plyMesh,
              y: plyMesh,
              z: plyMesh,
              s: plyMesh,
              attributesLoaded: false
            });
          }
        } else {
          // Store URLs for lazy loading
          for (
            let i = 0;
            i < this.projectConfig.project.optimization!.v2!.prismatic.loadcases.length;
            i++
          ) {
            const loadcase = this.projectConfig.project.optimization!.v2!.prismatic.loadcases[i];
            this.geometryProcessing.loadCaseUrls.prismatic.push({
              id: loadcase.id,
              x: loadcase.x,
              y: loadcase.y,
              z: loadcase.z,
              t: loadcase.t,
              s: loadcase.s
            });
            result.push({
              id: loadcase.id,
              t: plyMesh,
              x: plyMesh,
              y: plyMesh,
              z: plyMesh,
              s: plyMesh,
              attributesLoaded: false
            });
          }
        }
        this.progressService.updateTaskProgress('optimization-info', 90, 'Prismatic loadcase info prepared');
        return result;
      };

      const [organic, prismatic] = await Promise.all([organicGenerator(), prismaticGenerator()]);
      this.progressService.completeTask('optimization-info', 'Optimization info loaded');
      return { organic, prismatic };
    } else {
      // Legacy flow - keep the original behavior for now
      return this.getOptimizedAssemblyInfoLegacy();
    }
  }

  /**
   * Legacy loading method for flowVersion !== 2
   */
  private async getOptimizedAssemblyInfoLegacy(): Promise<any> {
    this.progressService.updateTaskProgress('optimization-info', 20, 'Loading legacy optimization info…');

    const progressTracker = {
      loaded: 0,
      total: 0,
      fileLoaded: new Map<string, number>(),
      fileTotal: new Map<string, number>()
    };

    const updateProgressMessage = (baseProgress: number, progressRange: number, label: string) => {
      const downloadProgress =
        progressTracker.total > 0 ? (progressTracker.loaded / progressTracker.total) * progressRange : 0;
      const progress = baseProgress + downloadProgress;
      const message = `${label}: ${this.geometryProcessing.formatBytes(progressTracker.loaded)} / ${this.geometryProcessing.formatBytes(progressTracker.total)}`;
      this.progressService.updateTaskProgress('optimization-info', progress, message);
    };

    const loader = new GLTFLoader();
    const result: { id: string; t: Mesh | null; x: Mesh | null; y: Mesh | null; z: Mesh | null; s: Mesh | null }[] =
      [];

    const loadMesh = async (url: string) => {
      try {
        const gltf = await loader.loadAsync(url);
        (gltf.scene.children[0] as Mesh).geometry.computeVertexNormals();
        return gltf.scene.children[0] as Mesh;
      } catch (error) {
        console.error(`Error loading mesh from ${url}:`, error);
        return null;
      }
    };

    this.progressService.updateTaskProgress('optimization-info', 25, 'Downloading loadcase data…');

    // Use Promise.all to run all load cases concurrently
    const loadcasePromises = this.projectConfig.project.optimization!.loadcases.map(async (loadcase) => {
      try {
        const [tMesh, xMesh, yMesh, zMesh, sMesh] = await Promise.all([
          loadMesh(loadcase.t),
          loadMesh(loadcase.x),
          loadMesh(loadcase.y),
          loadMesh(loadcase.z),
          loadMesh(loadcase.s)
        ]);

        const newResult = { id: loadcase.id, t: tMesh, x: xMesh, y: yMesh, z: zMesh, s: sMesh };

        try {
          const attributes = await this.downloadWithProgress(
            loadcase.attributes,
            progressTracker,
            () => updateProgressMessage(25, 70, 'Downloading loadcase data'),
            'json'
          );
          Object.assign(newResult, { attributes, attributesLoaded: true });
        } catch (error) {
          console.error(`Error loading attributes for loadcase ${loadcase.id}:`, error);
        }

        return newResult;
      } catch (error) {
        console.error(`Error processing loadcase ${loadcase.id}:`, error);
        return null;
      }
    });

    const loadcaseResults = await Promise.all(loadcasePromises);
    loadcaseResults.forEach((loadcase) => {
      if (loadcase) result.push(loadcase);
    });

    this.progressService.completeTask('optimization-info', 'Legacy optimization info loaded');
    return result;
  }

  /**
   * Lazy load attributes for a specific loadcase
   * @param loadcaseId - The ID of the loadcase to load
   * @param type - 'organic' or 'prismatic'
   * @param engine - The engine instance for updating optimized info
   * @returns The loaded attributes data
   */
  async loadLoadCaseAttributes(
    loadcaseId: string,
    type: 'organic' | 'prismatic' = 'organic',
    progressCallback?: (progress: number) => void,
    engine?: any
  ): Promise<any> {
    const cacheKey = `${type}_${loadcaseId}`;

    // Already successfully loaded — reuse engine-side attributes.
    // Still counts as a batch unit: a cached case is one of the N the caller
    // declared, so the bar must advance past it rather than stall.
    if (this.loadedLoadCases.has(cacheKey)) {
      this.markBatchUnitComplete();
      this.refreshAggregateLoadCaseProgress();
      progressCallback?.(100);
      return null;
    }

    // A concurrent call for the same key is already running. Await it so
    // the caller's guard on `loadCaseData.attributes` sees the resolved
    // state, and avoid double-fetching / racing the engine-side writes.
    const existing = this.loadCaseInflight.get(cacheKey);
    if (existing) {
      try {
        await existing;
      } catch {
        /* the owning call already surfaced the error; fall through */
      }
      this.markBatchUnitComplete();
      this.refreshAggregateLoadCaseProgress();
      progressCallback?.(100);
      return null;
    }

    const urls =
      type === 'organic'
        ? this.geometryProcessing.loadCaseUrls.organic
        : this.geometryProcessing.loadCaseUrls.prismatic;
    const loadcaseUrl = urls.find((lc) => lc.id === loadcaseId);

    if (!loadcaseUrl) {
      console.warn(`Loadcase ${loadcaseId} not found in ${type} URLs`);
      this.markBatchUnitComplete();
      this.refreshAggregateLoadCaseProgress();
      return null;
    }

    const promise = this._loadLoadCaseAttributesInternal(
      cacheKey,
      loadcaseId,
      type,
      loadcaseUrl,
      progressCallback,
      engine,
    );
    this.loadCaseInflight.set(cacheKey, promise);
    try {
      return await promise;
    } finally {
      this.loadCaseInflight.delete(cacheKey);
    }
  }

  private async _loadLoadCaseAttributesInternal(
    cacheKey: string,
    loadcaseId: string,
    type: 'organic' | 'prismatic',
    loadcaseUrl: { id: string; t: string; x: string; y: string; z: string; s?: string },
    progressCallback?: (progress: number) => void,
    engine?: any,
  ): Promise<any> {
    // Yield to microtask queue before setting loading state.
    // This ensures Angular's current CD cycle completes before the state changes,
    // preventing NG0100 when called during component initialization.
    await Promise.resolve();
    // Register this fetch with the aggregate tracker instead of resetting the
    // shared scalar — another load case may already be downloading.
    this.loadCaseByteTotals.set(cacheKey, { loaded: 0, total: 0 });
    this.loadCaseDataLoading = true;
    this.loadCaseDataMessage = `Loading ${type} loadcase data...`;
    progressCallback?.(0);

    const progressTracker = {
      loaded: 0,
      total: 0,
      fileLoaded: new Map<string, number>(),
      fileTotal: new Map<string, number>()
    };

    let progressRafId: number | null = null;

    const updateProgress = () => {
      const latestPercentage =
        progressTracker.total > 0 ? Math.round((progressTracker.loaded / progressTracker.total) * 100) : 0;
      // `progressCallback` stays scoped to THIS load case — the comparison
      // view drives a per-panel bar from it. Only the shared scalar aggregates.
      // Scale download progress to 0-85% range, processing is 85-100%
      progressCallback?.(Math.round(latestPercentage * 0.85));

      this.loadCaseByteTotals.set(cacheKey, {
        loaded: progressTracker.loaded,
        total: progressTracker.total
      });

      // Coalesce UI property updates via requestAnimationFrame to prevent NG0100
      // Multiple XHR progress events per frame only apply the latest value
      if (progressRafId === null) {
        progressRafId = requestAnimationFrame(() => {
          progressRafId = null;
          this.refreshAggregateLoadCaseProgress();
        });
      }
    };

    try {
      let attributes: any;
      const tPromise = this.downloadWithProgress(loadcaseUrl.t, progressTracker, updateProgress, 'json');
      const safeFetch = async (url: string | undefined): Promise<any> => {
        if (!url) return null;
        try {
          return await this.downloadWithProgress(url, progressTracker, updateProgress, 'json');
        } catch {
          return null;
        }
      };
      // Mode-shape entries never carry an `s` URL (von Mises stress has no
      // meaning for a mode shape); static load cases mixed into a Free
      // Vibration run do, and must be fetched the same as a plain static run.
      const sPromise = loadcaseUrl.s
        ? this.downloadWithProgress(loadcaseUrl.s, progressTracker, updateProgress, 'json')
        : Promise.resolve(null);

      const [t, x, y, z, s] = await Promise.all([
        tPromise,
        safeFetch(loadcaseUrl.x),
        safeFetch(loadcaseUrl.y),
        safeFetch(loadcaseUrl.z),
        sPromise,
      ]);

      // Processing phase: 85-95%
      progressCallback?.(90);

      // A missing file is not always an HTTP error: the desktop interceptor
      // maps fs:readFile failures to a 200 response with a null body. Without
      // this guard the all-null attributes would be cached as "loaded" and
      // crash the texture loader (null.xmin) on every render attempt.
      if (t == null) {
        throw new Error(
          `Result files missing for loadcase ${loadcaseId} (${type}): disp_t returned no data`
        );
      }

      attributes = {
        x: x ?? t,
        y: y ?? t,
        z: z ?? t,
        t,
      };
      if (s) {
        attributes.s = s;
      }

      // Processing phase: 95%
      progressCallback?.(95);

      // Update the attributes in the correct optimizeInfoFlowVersion2 array based on type
      if (engine) {
        const targetArray =
          type === 'organic'
            ? engine.optimizeInfoFlowVersion2.organic
            : engine.optimizeInfoFlowVersion2.prismatic;

        if (targetArray) {
          const info = targetArray.find((i: any) => i.id === loadcaseId) as any;
          if (info) {
            info.attributes = attributes;
            info.attributesLoaded = true;
          }
        }

        // Also update in current assemblyOptimizedInfo if it matches
        const currentInfo = engine.assemblyOptimizedInfo.find((i: any) => i.id === loadcaseId) as any;
        if (currentInfo) {
          currentInfo.attributes = attributes;
          currentInfo.attributesLoaded = true;
        }
      }

      // Only set the "loaded" flag AFTER the engine-side writes succeed.
      // If anything above throws (or the engine was torn down mid-fetch),
      // the key stays absent from `loadedLoadCases` so a retry actually
      // re-fetches instead of being short-circuited against empty attrs.
      this.loadedLoadCases.set(cacheKey, true);

      // Drop this fetch from the aggregate. The flag only clears once the map
      // is empty, so a still-running sibling load keeps the bar up instead of
      // having it vanish the moment the first of several finishes.
      this.loadCaseByteTotals.delete(cacheKey);
      this.markBatchUnitComplete();
      if (this.loadCaseByteTotals.size === 0 && this.loadCaseBatchExpected === 0) {
        this.loadCaseDataMessage = 'Loadcase data loaded';
      }
      this.refreshAggregateLoadCaseProgress();
      progressCallback?.(100);

      return attributes;
    } catch (error) {
      if (progressRafId !== null) {
        cancelAnimationFrame(progressRafId);
        progressRafId = null;
      }
      console.error(`Error loading attributes for loadcase ${loadcaseId}:`, error);
      this.loadCaseByteTotals.delete(cacheKey);
      // A failed case still advances the batch — otherwise one bad load case
      // parks the bar short of 100% for the rest of the run.
      this.markBatchUnitComplete();
      if (this.loadCaseByteTotals.size === 0 && this.loadCaseBatchExpected === 0) {
        this.loadCaseDataMessage = 'Error loading loadcase data';
      }
      this.refreshAggregateLoadCaseProgress();
      throw error;
    }
  }

  /**
   * Check if a loadcase's attributes have been loaded
   */
  isLoadCaseLoaded(loadcaseId: string, type: 'organic' | 'prismatic' = 'organic'): boolean {
    const cacheKey = `${type}_${loadcaseId}`;
    return this.loadedLoadCases.has(cacheKey);
  }
}
